import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import {
  Row, Col, Typography, Popover,
  Divider, message, Select,
} from 'antd';
import PropTypes from 'prop-types';
import {
  DeleteOutlined,
  DragOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';

import BorderlessButton from '../../../common/buttons/BorderlessButton';
import CalculationDropdown from './CalculationDropdown';
import FormCheckbox from '../../FormDetails/FormCheckbox';
import DisplayText from '../../../common/text/DisplayText';
import TableFieldColumnPermissions from './TableFieldColumnPermissions';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import colors from '../../../constants/Colors';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import OntraccrTag from '../../../common/OntraccrTag';

import { TIME_ENTRY_PERMISSION_EXCLUDED_COLUMNS } from './TimeEntryTable/TimeEntryTable.constants';
import { TABLE_TYPES_WITH_COLUMN_PERMISSIONS } from '../../forms.constants';
import { isCustomDateColumn } from './tableColumnHelpers';

import { safeExtractFromObject } from '../../../helpers/helpers';

function TableFieldColumnCard({
  fieldId,
  sectionId,
  sections = [],
  id = '',
  orderIndex,
  name,
  isCalculation,
  isCustomText,
  isReadOnly,
  onDeleteClicked,
  configProps,
  setConfigProps,
  dataType,
  divisions,
  originalName,
  onTitleChanged,
  validateCustomTextName,
}) {
  const [draftName, setDraftName] = useState(name);

  useEffect(() => {
    setDraftName(name);
  }, [name, id]);

  const commitCustomTextName = useCallback(() => {
    const trimmed = draftName?.trim();

    if (!trimmed) {
      message.error('Column name is required');
      setDraftName(name);
      return;
    }

    const error = validateCustomTextName?.(trimmed, id);
    if (error) {
      message.error(error);
      setDraftName(name);
      return;
    }

    if (trimmed !== name) {
      onTitleChanged({ id, title: trimmed });
    }
  }, [draftName, name, id, onTitleChanged, validateCustomTextName]);

  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));

  const hasRedesign = !!settings?.uxRedesignPt1;

  const {
    formulaMap = {},
    tagsMap = {},
    columnPermissions = {},
  } = configProps;

  const col = useMemo(() => {
    const { columns = [] } = configProps;
    return columns.find((column) => column.key === id) || {};
  }, [configProps, id]);

  const formula = formulaMap[id];
  const tags = tagsMap[id] || [];

  const onTagsAndFormulaChange = ({ tags: newTags, formula: newFormula }) => {
    setConfigProps({
      ...configProps,
      tagsMap: {
        ...tagsMap,
        [id]: newTags,
      },
      formulaMap: {
        ...formulaMap,
        [id]: newFormula,
      },
    });
  };

  const onColumnSettingChange = useCallback((key, value) => {
    setConfigProps((prevConfigProps) => {
      const { columns = [] } = prevConfigProps;
      let newColumns = columns.map((column) => {
        if (column.key !== id) {
          return column;
        }
        return {
          ...column,
          [key]: value,
        };
      });

      // For TimeEntry tables, when sticky is toggled, reorder columns
      if (key === 'isSticky' && dataType === 'TimeEntry') {
        const updatedColumn = newColumns.find(col => col.key === id);
        const otherColumns = newColumns.filter(col => col.key !== id);
        const stickyColumns = otherColumns.filter(col => col.isSticky);
        const nonStickyColumns = otherColumns.filter(col => !col.isSticky);

        // Place updated column between sticky and non-sticky sections
        newColumns = [...stickyColumns, updatedColumn, ...nonStickyColumns];
      }

      return {
        ...prevConfigProps,
        columns: newColumns,
      };
    });
  }, [setConfigProps, id, dataType]);

  const onReadOnlyChange = useCallback((checked) => {
    onColumnSettingChange('isReadOnly', checked);
  }, [onColumnSettingChange]);

  const onColumnPermissionChange = useCallback((newPermissions) => {
    setConfigProps((prev) => ({
      ...prev,
      columnPermissions: {
        ...prev.columnPermissions,
        [id]: newPermissions,
      },
    }));
  }, [id]);

  const showColumnPermissions = useMemo(() => {
    if (isCustomText) return true;
    if (dataType === 'TimeEntry') {
      return !TIME_ENTRY_PERMISSION_EXCLUDED_COLUMNS.has(id);
    }

    return TABLE_TYPES_WITH_COLUMN_PERMISSIONS.has(dataType);
  }, [dataType, id, isCustomText]);

  const onNumericChange = useCallback((checked) => {
    setConfigProps((prevConfigProps) => {
      const { columns: prevColumns = [] } = prevConfigProps;
      const newColumns = prevColumns.map((column) => {
        if (column.key !== id) return column;
        return {
          ...column,
          isNumeric: checked,
          isCurrency: checked ? column.isCurrency : false,
        };
      });
      return {
        ...prevConfigProps,
        columns: newColumns,
      };
    });
  }, [setConfigProps, id]);

  const onCurrencyChange = useCallback((checked) => {
    onColumnSettingChange('isCurrency', checked);
  }, [onColumnSettingChange]);

  const onStickyChange = useCallback((checked) => {
    onColumnSettingChange('isSticky', checked);
  }, [onColumnSettingChange]);

  const isTimeEntry = dataType === 'TimeEntry';
  const isCustomDateTableColumn = isCustomDateColumn(col);

  const dateTimeOptions = useMemo(() => (
    (sections ?? []).reduce((acc, section) => {
      const { fields = [], name: sectionName } = section;
      return acc.concat(
        (fields ?? []).reduce((fieldAcc, field) => {
          const {
            id: fieldId,
            selectedType,
            configProps: {
              title: fieldTitle,
              hideDate,
            } = {},
          } = field;
          if (selectedType !== 'dateTime' || hideDate) return fieldAcc;
          return fieldAcc.concat([{
            label: `${sectionName} - ${fieldTitle}`,
            value: fieldId,
          }]);
        }, []),
      );
    }, [])
  ), [sections]);

  const dateLinkOptions = useMemo(() => [
    { value: 'currentDate', label: 'Current Date' },
    { value: 'dateTime', label: 'Date Time Field', disabled: !dateTimeOptions.length },
  ], [dateTimeOptions]);

  const isDateLinked = col.dateLinkType === 'currentDate' || col.dateLinkType === 'dateTime';

  const onDateLinkCheckChange = useCallback((checked) => {
    setConfigProps((prevConfigProps) => {
      const { columns: prevColumns = [] } = prevConfigProps;
      return {
        ...prevConfigProps,
        columns: prevColumns.map((column) => {
          if (column.key !== id) return column;
          return {
            ...column,
            dateLinkType: checked ? 'currentDate' : 'none',
            linkedDateTimeFieldId: undefined,
          };
        }),
      };
    });
  }, [id]);

  const onDateLinkTypeChange = useCallback((value) => {
    setConfigProps((prevConfigProps) => {
      const { columns: prevColumns = [] } = prevConfigProps;
      return {
        ...prevConfigProps,
        columns: prevColumns.map((column) => {
          if (column.key !== id) return column;
          return {
            ...column,
            dateLinkType: value,
            ...(value !== 'dateTime' ? { linkedDateTimeFieldId: undefined } : {}),
          };
        }),
      };
    });
  }, [id]);

  const columnSettings = useMemo(() => {
    const settings = (
      <Col style={{ width: showColumnPermissions ? '30em' : '15em' }}>
        <Row>
          <DisplayText title="Column Settings" />
        </Row>
        <FormCheckbox
          title="Hide in PDF"
          value={id === 'attachments' ? true : col.isHideInPDF}
          onCheckChange={(val) => onColumnSettingChange('isHideInPDF', val)}
          tooltip="Check this to hide this column in the PDF preview and export"
          disabled={id === 'attachments'}
        />
        {
          isCalculation && (
            <>
              <FormCheckbox
                title="Is Currency?"
                value={col.isCurrency}
                onCheckChange={(val) => onColumnSettingChange('isCurrency', val)}
                tooltip="Check this box if you want the result to have a '$' prefix (e.g. $25.00)"
              />
              <FormCheckbox
                title="Round to Whole Number"
                value={col.roundCalculation}
                onCheckChange={(val) => onColumnSettingChange('roundCalculation', val)}
              />
              <Select
                style={{ width: '100%', marginTop: 5 }}
                value={col.roundingSetting ?? 'roundUp'}
                onChange={(val) => onColumnSettingChange('roundingSetting', val)}
                disabled={!col.roundCalculation}
                options={[
                  { value: 'roundUp', label: 'Round Up' },
                  { value: 'roundDown', label: 'Round Down' },
                ]}
              />
            </>
          )
        }
        {
          isCustomText && !isCustomDateTableColumn && (
            <>
              <FormCheckbox
                title="Force Numeric"
                value={col.isNumeric}
                onCheckChange={onNumericChange}
                tooltip="Check this to restrict input to numeric values"
              />
              <FormCheckbox
                title="Is Currency"
                value={col.isCurrency}
                onCheckChange={onCurrencyChange}
                disabled={!col.isNumeric}
                tooltip="Check this to format the value as currency with a $ prefix and two decimals"
              />
            </>
          )
        }
        {
          isCustomDateTableColumn && (
            <>
              <FormCheckbox
                title="Link Date"
                value={isDateLinked}
                onCheckChange={onDateLinkCheckChange}
                tooltip="Check this to link the column to the current date or a date time field"
              />
              <Select
                style={{ width: '100%', marginTop: 5 }}
                value={isDateLinked ? col.dateLinkType : undefined}
                options={dateLinkOptions}
                onChange={onDateLinkTypeChange}
                disabled={!isDateLinked}
                placeholder="Select a link"
              />
              {col.dateLinkType === 'dateTime' && (
                <Select
                  style={{ width: '100%', marginTop: 5 }}
                  allowClear
                  placeholder="Select a Date Time Field"
                  value={col.linkedDateTimeFieldId}
                  options={dateTimeOptions}
                  onChange={(val) => onColumnSettingChange('linkedDateTimeFieldId', val)}
                  disabled={!isDateLinked}
                />
              )}
            </>
          )
        }
        {
          isTimeEntry && (
          <FormCheckbox
            title="Sticky Column"
            value={col.isSticky}
            onCheckChange={onStickyChange}
            tooltip="Check this to make this column sticky (fixed to the left side) when scrolling horizontally"
          />
          )
        }
        {showColumnPermissions && (
          <>
            <Divider />
            <TableFieldColumnPermissions
              divisions={divisions}
              permissions={columnPermissions[id]}
              onPermissionChanged={onColumnPermissionChange}
            />
          </>
        )}
      </Col>
    );

    return settings;
  }, [
    col,
    isCalculation,
    isCustomText,
    isCustomDateTableColumn,
    isDateLinked,
    dateLinkOptions,
    dateTimeOptions,
    divisions,
    showColumnPermissions,
    columnPermissions,
    id,
  ]);

  const customNameDataTypes = ['Equipment', 'Materials', 'Buckets', 'TimeEntry', 'ScheduleOfValues'];
  const showRenameInput = customNameDataTypes.includes(dataType) || isCustomText;

  return (
    <Draggable draggableId={id.toString()} index={orderIndex}>
      {({ draggableProps, dragHandleProps, innerRef }) => (
        <div
          ref={innerRef}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...draggableProps}
          className="boards-status-card"
        >
          <Row justify="space-between" align="middle">
            <Col flex="18px">
              <Row justify="center" style={{ height: '100%', paddingRight: 5 }} align="middle">
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <DragOutlined {...dragHandleProps} className="boards-status-dragger" />
              </Row>
            </Col>
            <Col flex="auto">
              {showRenameInput ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginLeft: 5,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isCustomText ? (
                      <OnTraccrTextInput
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        onBlur={commitCustomTextName}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        style={{ maxWidth: '300px' }}
                      />
                    ) : (
                      <OnTraccrTextInput
                        value={name}
                        onChange={(e) => onTitleChanged({ id, title: e.target.value })}
                        style={{ maxWidth: '300px' }}
                      />
                    )}
                    {isTimeEntry && col.isSticky && (
                      <OntraccrTag color="blue">Sticky</OntraccrTag>
                    )}
                  </div>
                  {name !== originalName && !isCustomText && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                  }}
                  >
                    <Typography.Text type="secondary">System Default Name: </Typography.Text>
                    <OntraccrTag
                      style={
                        hasRedesign
                          ? {}
                          : { backgroundColor: colors.ONTRACCR_RED, color: 'white' }
                      }
                    >
                      {originalName}
                    </OntraccrTag>
                  </div>
                  )}
                </div>
              ) : (
                <div style={{ marginLeft: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text>
                      {name}
                    </Typography.Text>
                    {isTimeEntry && col.isSticky && (
                      <OntraccrTag color="blue">Sticky</OntraccrTag>
                    )}
                  </div>
                </div>
              )}
            </Col>
            {isCalculation && (
            <Col flex="350px">
              <CalculationDropdown
                id={fieldId}
                sectionId={sectionId}
                onTagsAndFormulaChange={onTagsAndFormulaChange}
                formula={formula}
                tags={tags}
                sections={sections}
                calculationColumn={id}
                hideExplanation
              />
            </Col>
            )}
            {id === 'quantityAllocated' && (
              <Col flex="350px">
                <OnTraccrCheckbox
                  value={isReadOnly}
                  onChange={onReadOnlyChange}
                  label="Read Only"
                />
              </Col>
            )}
            <Col flex="30px" />
            <Col flex="30px" style={{ position: 'relative' }}>
              <Popover
                trigger="click"
                content={columnSettings}
                placement="topLeft"
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
              >
                <SettingOutlined style={{ margin: 0 }} />
              </Popover>
            </Col>
            <Col flex="30px">
              <BorderlessButton
                iconNode={<DeleteOutlined style={{ margin: 0, color: 'red' }} />}
                onClick={onDeleteClicked}
              />
            </Col>
          </Row>
        </div>
      )}
    </Draggable>
  );
}

export default TableFieldColumnCard;

TableFieldColumnCard.propTypes = {
  fieldId: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  id: PropTypes.string,
  orderIndex: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  isCalculation: PropTypes.bool,
  isCustomText: PropTypes.bool,
  onDeleteClicked: PropTypes.func.isRequired,
  configProps: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    formulaMap: PropTypes.shape({}),
    tagsMap: PropTypes.shape({}),
    columnPermissions: PropTypes.shape({}),
  }),
  setConfigProps: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  isReadOnly: PropTypes.bool,
  dataType: PropTypes.string,
  divisions: PropTypes.arrayOf(PropTypes.string),
  originalName: PropTypes.string.isRequired,
  onTitleChanged: PropTypes.func.isRequired,
  validateCustomTextName: PropTypes.func,
};

TableFieldColumnCard.defaultProps = {
  id: '',
  isCalculation: false,
  isCustomText: false,
  configProps: {},
  sections: [],
  isReadOnly: false,
  dataType: null,
  divisions: undefined,
  validateCustomTextName: null,
};
